// import components
import { Icon } from '@sdb/planning-ui';
import { ContextMenu } from 'components/mrp/components/ContextMenu';
import Tooltip from 'components/shared/Tooltip';
import { SummaryCard } from 'components/mrp/components/SummaryCard';
import NonRecurringIconBlack from 'components/mrp/components/Calendar/components/NonRecurringIconBlack';
import RecurrenceConfirmationDialog from 'components/roster-portal/appointments-overview/components/RecurrenceConfirmationDialog/RecurrenceConfirmationDialog';
import NotificationConfirmationDialog from '../NotificationConfirmationDialog';

// import external libraries
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import { useOutsideTreatmentAppointmentActions } from './OutsideTreatmentAppointmentCard.hooks';

// import utils
import { convertEventResponseToCalendarEvent } from 'components/roster-portal/appointments-overview/components/AppointmentRowTooltip/AppointmentRowTooltip.utils';
import { getParticipantSummary } from './OutsideTreatmentAppointmentCard.utils';
import { getHoursMinutes } from 'utils/date';

// import styles
import {
  ActivityName,
  AppointmentTime,
  Card,
  OverflowBadge,
  Participant,
  SignalIcon,
  SignalOverlay,
  SignalStack,
  TitleRow
} from './OutsideTreatmentAppointmentCard.styles';

// import types
import type { MouseEvent, ReactNode } from 'react';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { ContextMenuAction } from 'components/mrp/components/ContextMenu/ContextMenu.types';
import type { OutsideTreatmentAppointmentCardProps } from './OutsideTreatmentAppointmentCard.types';

/**
 * One appointment outside treatment, as a card in the week-detail — modelled on the afsprakenplanning
 * cards: the activity and the other participants (the care receiver of the column is left off). Hovering
 * shows the full appointment detail, the same tooltip afsprakenplanning uses. Right-clicking opens the
 * very same context menu afsprakenplanning uses — the unassign and delete actions, with its icons and
 * wording ("Entiteit loskoppelen" / "Afspraak verwijderen") — and a recurring appointment first asks
 * only-this vs this-and-subsequent for a series. A non-editable appointment is greyed out and its actions
 * disabled, the same way afsprakenplanning reads a read-only appointment. A recurring appointment also
 * carries afsprakenplanning's recurrence marker in the top-right corner.
 */
const OutsideTreatmentAppointmentCard = ({
  event,
  careReceiverId,
  entityType = 'CLIENT',
  accentColor
}: OutsideTreatmentAppointmentCardProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  // The menu labels are taken verbatim from afsprakenplanning (the mrp calendar), so the wording and
  // symbols stay identical to the ones shown when right-clicking an appointment there.
  const { t: tAppointmentMenu } = useTranslation('common', {
    keyPrefix: 'mrp'
  });

  const {
    onDelete,
    onUnassign,
    onEdit,
    pendingAction,
    confirmRecurrence,
    cancelRecurrence,
    notificationMessages,
    confirmNotifications,
    cancelNotifications
  } = useOutsideTreatmentAppointmentActions(event, careReceiverId, entityType);

  // The right-click menu is positioned at the cursor, the way a context menu should be.
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Tracks whether this card's menu is open, so the card can show it is the one being acted on.
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const rememberCursor = (mouseEvent: MouseEvent<HTMLElement>) =>
    setMenuPosition({ top: mouseEvent.clientY, left: mouseEvent.clientX });

  // The same actions afsprakenplanning offers, with its icons, wording and order (edit sits between the
  // disconnect and the delete). They are disabled — not hidden — on a non-editable appointment, matching
  // how afsprakenplanning greys out its read-only rows.
  // Named after the entity the card sits under rather than a generic "entity": an appointment carries
  // several, and the planner has to know which one this would take off it.
  const unassignLabel = t('unassignEntityTitle', {
    entity: tAppointmentMenu(`entityTypeMap.${entityType}.single`)
  });

  const menuActions: ContextMenuAction[] = [
    {
      value: unassignLabel,
      icon: { iconType: 'link-slash' as IconType },
      disabled: !event.isEditable,
      onClick: onUnassign
    },
    {
      value: tAppointmentMenu('editAppointment'),
      icon: { iconType: 'pen-to-square' as IconType },
      disabled: !event.isEditable,
      onClick: onEdit
    },
    {
      value: tAppointmentMenu('deleteAppointment'),
      icon: { iconType: 'trash-can' as IconType },
      disabled: !event.isEditable,
      onClick: onDelete
    }
  ];

  // The participants exactly as afsprakenplanning words them on a card too small to draw them out —
  // counts per type, the single name when there is only one — so the same appointment reads the same in
  // both places. The entity this card sits under is dropped from its own list but still counted.
  const participantLine = getParticipantSummary(
    event,
    careReceiverId,
    entityType
  );

  const timeRange = `${getHoursMinutes(event.start)} - ${getHoursMinutes(
    event.end
  )}`;

  // The recurrence prompt is shared with afsprakenplanning; its wording depends on the action behind it.
  const recurrenceLabels = {
    delete: {
      title: t('deleteAppointmentTitle'),
      description: t('recurrenceDeleteDescription'),
      confirmLabel: t('deleteAppointmentConfirm')
    },
    unassign: {
      title: unassignLabel,
      description: t('recurrenceUnassignDescription'),
      confirmLabel: t('unassignCareReceiverConfirm')
    }
  };
  const activeLabels = pendingAction ? recurrenceLabels[pendingAction] : null;

  // The signal markers pinned in the top-right corner, stacked downward in the same order
  // afsprakenplanning lists them: the recurrence marker first (a repeat icon for a series occurrence,
  // its struck-through variant for an exception), then a note marker when the appointment has a note.
  // Only these two are shown for now; keeping afsprakenplanning's order means further signals can slot
  // into the same sequence later without reshuffling.
  const hasNote = !!event.notePublic || !!event.noteInternal;
  const signals: { key: string; label: string; icon: ReactNode }[] = [];
  if (event.appointmentType === 'SERIES_INSTANCE') {
    signals.push({
      key: 'series',
      label: t('series'),
      icon: <Icon type="arrows-repeat" />
    });
  } else if (event.appointmentType === 'SERIES_EXCEPTION') {
    signals.push({
      key: 'seriesException',
      label: t('seriesException'),
      icon: <NonRecurringIconBlack />
    });
  }
  if (hasNote) {
    signals.push({ key: 'note', label: t('note'), icon: <Icon type="note" /> });
  }
  // Both text rows keep off the same corner: a second marker stacks down into the participants.
  const hasSignals = signals.length > 0;

  return (
    <>
      <ContextMenu
        actions={menuActions}
        clickEvent="onContextMenu"
        id={event.id}
        anchorPosition={menuPosition ?? undefined}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
      >
        <Tooltip
          arrow
          enterDelay={100}
          enterNextDelay={100}
          // The exact detail popup afsprakenplanning shows — icons, Wanneer/Wat & Waar/Medewerkers/
          // Patiënten/Middelen, sync status, audit — so hovering reads the same in both places.
          title={
            <SummaryCard
              event={convertEventResponseToCalendarEvent(event)}
              interactable={false}
            />
          }
        >
          <Card
            // The card carries no role of its own, so this is what the tests reach it by — its title is
            // now nested a level deeper, inside the time-and-name row.
            data-testid="appointment-card"
            onContextMenu={rememberCursor}
            isReadOnly={!event.isEditable}
            accentColor={accentColor}
            data-selected={isMenuOpen ? 'true' : undefined}
          >
            <TitleRow
              // Only held free when this card actually has markers: with nothing in the corner the
              // time runs to the card edge rather than stopping short of an empty column. The two
              // cases therefore end 22px apart, which is the price of not leaving a visible gap.
              reserveMarkerSpace={hasSignals}
            >
              <ActivityName
                variant="body2"
                noWrap
                isReadOnly={!event.isEditable}
              >
                {event.activity.name}
              </ActivityName>
              <AppointmentTime variant="body2" isReadOnly={!event.isEditable}>
                {timeRange}
              </AppointmentTime>
            </TitleRow>
            {/* Only shown when the appointment has participants besides this card's own entity. */}
            {!!participantLine && (
              <Participant
                variant="caption"
                color="text.secondary"
                reserveMarkerSpace={hasSignals}
              >
                {participantLine}
              </Participant>
            )}
            {hasSignals && (
              <SignalOverlay>
                <SignalStack
                  className="signal-stack"
                  data-signal-count={signals.length}
                >
                  {signals.map((signal) => (
                    <SignalIcon
                      key={signal.key}
                      className="signal-icon"
                      role="img"
                      aria-label={signal.label}
                    >
                      {signal.icon}
                    </SignalIcon>
                  ))}
                  {/* Rendered for the container-query overflow fallback above: it stays hidden until
                      the markers can't fit, then replaces them with this count. */}
                  {signals.length > 1 && (
                    <OverflowBadge className="overflow-badge">
                      +{signals.length}
                    </OverflowBadge>
                  )}
                </SignalStack>
              </SignalOverlay>
            )}
          </Card>
        </Tooltip>
      </ContextMenu>
      {activeLabels && (
        <RecurrenceConfirmationDialog
          open
          title={activeLabels.title}
          description={activeLabels.description}
          confirmLabel={activeLabels.confirmLabel}
          onConfirm={confirmRecurrence}
          onCancel={cancelRecurrence}
        />
      )}
      {notificationMessages && (
        <NotificationConfirmationDialog
          open
          title={t('unassignNotificationsTitle')}
          description={t('unassignNotificationsDescription')}
          messages={notificationMessages}
          confirmLabel={t('continueAnyway')}
          onConfirm={confirmNotifications}
          onCancel={cancelNotifications}
        />
      )}
    </>
  );
};

export default OutsideTreatmentAppointmentCard;
